/* eslint-disable import/prefer-default-export, max-len */
// Source of truth for the in-app per-step explainer copy on the
// Page Optimization wizard. Edit this file to update what users see
// at the top of each tab. See:
// docs/superpowers/specs/2026-05-07-page-optimization-step-docs-design.md

export const stepDocs = {
  overview: {
    label: 'Overview',
    purpose: "A quick read on this page — its slug, page type, content extraction status, and which integrations (GSC, GA, WordPress) are wired up. Nothing happens here automatically; it's just orientation before you start working through the wizard.",
    inputs: [
      'The page record itself. If it was imported from the sitemap or pasted in manually, basic metadata is already populated.',
      'Optionally, content extraction (the "Extract Content" button at the top) — needed before downstream steps that reason about your page\'s headings or word count.'
    ],
    process: [
      'Read-only display of page type (service page, location page, etc.), category, status, GSC/GA enable flags, and whether content has been extracted.',
      'The Re-extract button at the top of the panel re-fetches the live HTML and re-parses headings/meta/body — useful if the page has changed since the last extraction.'
    ],
    outputs: [
      'Nothing on its own. This tab is informational. Re-extraction does update the underlying `extracted_content`, `headings`, and `meta_*` fields used by Counting and Topics later.'
    ],
    gotchas: [
      'If Counting or Topics show stale numbers, come back here and Re-extract first.',
      'This tab is never locked — every other tab is.'
    ]
  },

  analytics: {
    label: 'Analytics',
    purpose: 'See how this page actually performs in Google before you decide whether to optimize it. Clicks, impressions, CTR, position from Search Console; sessions and conversions from GA. The signal that drives the priority score on the page list.',
    inputs: [
      'GSC enabled for the website AND for this specific page URL.',
      'GA4 enabled for the website (optional but useful).',
      'A daily sync to have fired at least once — analytics data lags 2–3 days.'
    ],
    process: [
      'Renders the page-level GSC + GA stats over the selected window.',
      'Surfaces top queries this page already ranks for (which can inform Keywords selection later).',
      'Charts trends so you can see if performance is rising, flat, or declining.'
    ],
    outputs: [
      'No DB writes. Pure visualization.',
      'Indirectly: the priority score on the page list is computed from the same underlying data.'
    ],
    gotchas: [
      'If you see "No data" and you expect data, check that the page URL is enabled in GSC (Sites → Settings) and that the daily sync has run.',
      'GSC reports up to 90 days of history by default. Older data may be unavailable.'
    ]
  },

  keywords: {
    label: 'Keywords',
    purpose: 'Pick the single primary keyword this page should target. The keyword you choose here is the input for everything downstream — SERP analysis, competitor selection, content strategy, counting, headings, meta. Choose well.',
    inputs: [
      'Extracted page content (run Re-extract on the Overview tab first if needed).',
      'Business profile: category, services and locations decide which keywords fit this page.',
      'Sibling pages on the website, used to spot keywords another page already targets.'
    ],
    process: [
      '"Run Keyword Research" generates candidate keywords from your page content, business context and Search Console queries, then enriches them with search volume, CPC and competition.',
      'Each candidate gets a page-fit label: Homepage Primary, Better for Service Page, Better for Location Page, Supporting Keyword, Informational, or Competitor Brand. "Near me" and narrowing terms (inpatient, chaplain, palliative...) push a keyword to a service page.',
      'Eligibility: only a keyword with the right fit, enough relevance, no competitor brand and no strong overlap with a sibling page can be the Recommended Primary. Overlaps show as "overlaps /slug".',
      'Confirmed zero-volume keywords are marked and down-weighted; missing metrics are shown as unavailable, not zero.',
      'You review the ranked list and select one. Your selection is stored on the page record.'
    ],
    outputs: [
      'A `selected_keyword_candidate_id` on the website page. Every downstream tab reads this.',
      'A `keyword_research` record with all candidates and their scoring (fit, eligibility, ownership, demand status), so you can pick a different one later without re-running.'
    ],
    gotchas: [
      'If no candidate is eligible, nothing is marked Recommended Primary and a warning asks you to review the seeds. You can still select any keyword manually.',
      "Changing the SELECTED keyword after SERP analysis prompts a re-analysis, which discards Competitors → Strategy → Counting → Topics → Structure for this page. Don't change it casually after step 5."
    ]
  },

  serp: {
    label: 'SERP Analysis',
    purpose: 'Figure out what Google is actually rewarding for your keyword: search intent, the kinds of pages that rank (homepages, service pages, location pages, directories, government listings), and which SERP features show up. This shapes everything else.',
    inputs: [
      'A selected primary keyword (from the Keywords tab).',
      'A selected SERP location from the page target, linked page locations, business locations, or United States fallback.'
    ],
    process: [
      'Fetches the top organic results plus SERP feature blocks (AI Overview, People Also Ask, Local Pack, related searches...) for the selected location.',
      'Each organic result gets a page-type pill from its URL and title: Homepage, Service Page, Location Page, Service + Location Page, Directory, Government Listing, Informational, or Other.',
      'The format summary counts those types. When no type has a clear majority the SERP is reported as mixed (for example "Local commercial provider pages dominate: 3 location pages, 2 service pages...").',
      'An LLM classifies intent (informational/commercial/transactional/mixed). Result-type counts in the evidence always come from the deterministic classification.',
      'Related searches that name a competitor brand (e.g. "vna hospice dallas") are listed separately as brand queries in the market-research section, so they are not mistaken for target keywords.'
    ],
    outputs: [
      'A `serp_analysis` record with location, pull timestamp, per-result page types, format distribution, intent, feature findings, PAA questions and AI Overview text + sources.',
      'Auto-confirms on completion, which unlocks the Competitors tab.'
    ],
    gotchas: [
      'A format-mismatch warning appears only when one page type clearly dominates and your page is a different type.',
      'Re-analyzing (or changing the keyword) discards everything downstream (Competitors → Strategy → Counting → Topics → Structure).',
      'For multi-location clients, confirm the selected SERP location before running.'
    ]
  },

  competitors: {
    label: 'Competitors',
    purpose: "Build a model of what's working for the top organic results: extract their content, grade quality, set benchmarks. Everything you optimize from here on is \"match or beat the consensus of the verified local providers.\"",
    inputs: [
      'A confirmed SERP analysis (organic results to choose from).',
      'Working URLs (some competitor sites block scrapers — see Gotchas).'
    ],
    process: [
      'Every result is classified: Direct Local Business, Government Listing, National Organization, Directory / Aggregator, Social Profile, Lead-Gen, or Client-Owned Profile. Only direct local businesses are recommended; listings (e.g. Medicare.gov, Yelp) are shown as not recommended and excluded from benchmarks.',
      'You select competitors (recommended ones pre-selected) or add URLs. Background workers extract content, headings, meta, images, links and schema.',
      'Each page is graded A–F per dimension. Heading relevance (do the headings target the service and location?) and heading structure (H1 count, skipped levels) are separate grades; structure counts half as much.',
      'Each competitor shows a tier (elevated / standard / deprioritized / excluded) with the reason, e.g. "Standard: overall quality C (weakest: meta D...)" or "Set manually".',
      'Benchmarks (word count, heading counts, E-E-A-T, links...) are weighted by tier across verified, non-excluded pages. You approve the quality profile; that unlocks Strategy.'
    ],
    outputs: [
      'Verified `competitor_pages` with classification, tier + tier reason, extracted content and quality grades. Used by Strategy, Counting, Topics, and Structure.',
      '`competitor_benchmarks` (e.g. target word count) used to score over-optimization later.',
      'An approved quality profile unlocks the Keyword Strategy tab (at least 3 verified, graded competitors are required).'
    ],
    gotchas: [
      'Extraction can fail on JavaScript-heavy sites or sites that block scrapers. Replace those URLs with the next-best result and re-run.',
      'A tier you change by hand is kept when grades are recomputed.',
      'E-E-A-T signals are checked against a local-service profile (license, reviews, phone, service area...) for provider pages and an article profile for guides.'
    ]
  },

  strategy: {
    label: 'Keyword Strategy',
    purpose: 'Build the target keyword list: the keywords your page should mention (and roughly how often) based on what the verified competitor pages actually use. This is the source of truth for Counting and the keyword-target locks in Structure.',
    inputs: [
      'An approved competitor quality profile (Competitors tab).',
      'Extracted competitor content (used to mine keyword usage).',
      'Business profile and sibling pages (used for claim and ownership checks).'
    ],
    process: [
      'An LLM proposes a keyword universe; keywords are tiered (core / high_value / supporting / long_tail) and cross-referenced with competitor usage. Phrases found only in competitor headings are added for review.',
      'Every item is checked and flagged: Competitor brand, Employment intent (jobs, careers), incompatible intent (login, reviews, phone number...) and malformed fragments are excluded.',
      'Claims the business profile does not confirm (24/7, affordable, family owned, pediatric, accredited...) are set to evaluate with "Requires verification".',
      'Keywords a sibling page already targets show "Better targeted by /slug".',
      'Keywords that mean the same thing ("dallas hospice care", "hospice care dallas tx") are grouped into one concept under a canonical keyword; variants follow the canonical\'s decision.',
      'You review, include/exclude, and approve.'
    ],
    outputs: [
      'A `keyword_strategy` with `keyword_strategy_items` carrying tier, bucket, competitor usage, flags, concept key and canonical status.',
      'Approving the strategy unlocks the Counting tab.'
    ],
    gotchas: [
      'Flagged exclusions are locked: competitor usage or search volume never flips them back to include. You can still override by hand.',
      'Keywords you add manually are checked with the same rules.',
      'Re-running strategy generation discards Counting + Structure work for this page.'
    ]
  },

  counting: {
    label: 'Counting',
    purpose: 'Count how often your current page mentions each target keyword vs how often the competitors mention it, with the same counting rules applied to both. Surfaces the gap you need to close.',
    inputs: [
      'An approved keyword strategy (target keyword list).',
      'Extracted client and competitor content (at least some readable page text).'
    ],
    process: [
      'Approved keywords are reduced to one row per concept: the canonical keyword gets the target and its variants are counted with it, so "hospice dallas tx" and "dallas hospice care" share one target.',
      'Keywords that are not counted are listed under Filtered with the reason (competitor brand, employment intent, requires verification, owned by another page, malformed, not approved).',
      'The same occurrence count runs on your page and each benchmark competitor, with placements (first 100 words, headings, bold, alt text, anchors).',
      'Deltas: heading gaps, body under/on-target/over, placement gaps and TF-IDF notes. Over-optimization is flagged when your count is well above the competitor average.',
      'A delta summary prioritizes the work (AI-written, with a rule-based fallback). A verification checklist confirms all keywords are present and the math adds up.'
    ],
    outputs: [
      'A `keyword_occurrence` record with concept rows, filtered keywords, deltas, over-optimization flags, summary and verification items. Used by Structure to lock targets.',
      'Completion unlocks the Topics tab.'
    ],
    gotchas: [
      'Excluded and filtered keywords never get a target. If one should be counted, include it on the Strategy tab and re-run.',
      'If your page changed and counts look wrong, Re-extract on Overview before re-running counting.',
      'Over-optimization flags are advisory — review each one before locking targets in Structure.'
    ]
  },

  topics: {
    label: 'Topics',
    purpose: 'Identify the content TOPICS your page is missing relative to the SERP winners — the semantic gaps, not just keyword gaps — and say where each one belongs.',
    inputs: [
      'Verified, non-excluded competitor pages (Competitors tab).',
      'Extracted client content, the business profile and sibling pages.'
    ],
    process: [
      'Competitor H2–H4 headings are normalized into topics. Presentation headings ("What We Do", "How Can We Help?") are dropped and numbered steps are merged into one process topic.',
      'Each topic is categorized: must_add, should_add, could_add, covered, verify_eligibility (a program, accreditation or service the business profile does not confirm, e.g. CHAP, pediatric, music therapy), or competitor_specific (another provider\'s brand, blog, podcast or program — do not copy).',
      'Each topic gets a destination: add to this page, FAQ, resource page, internal link only (a sibling page already covers it, e.g. /palliative-care/), dedicated service page, or not applicable.',
      'Word count estimates and internal link suggestions are added. You select topics (must_add pre-checked) and approve.'
    ],
    outputs: [
      "A `semantic_analysis` record with the topic matrix (category, flags, destination, competitor sources) and the user's selections. Used by Structure to plan headings.",
      'Approval unlocks the Structure tab.'
    ],
    gotchas: [
      'Verify eligibility before adding a flagged topic; adding the service to the business profile clears the flag on the next run.',
      'Topics routed elsewhere (resource page, dedicated page, internal link) should not become H2s on this page.',
      "Word count budgets are estimates. Don't add every optional topic — pick the ones that fit the business."
    ]
  },

  structure: {
    label: 'Structure',
    purpose: "Lock in the on-page SEO foundations — title tag, meta description, H1, full heading structure (H2/H3/H4), and target keyword counts — before you hand any of it to the content rewriter. This step's locks are the contract the Content tab will honor verbatim.",
    inputs: [
      'A locked-in keyword (Keywords tab).',
      'An approved keyword strategy (Strategy tab).',
      'An approved topic plan (Topics tab).',
      'Counting results (provides target counts and over-optimization context).'
    ],
    process: [
      'Five sub-steps in order: Title Tag → Meta Description → H1 → Heading Structure → Keyword Verification → Target Lock.',
      'For each of Title/Meta/H1/Headings, an LLM generates options (3 titles, 2 metas, 3 H1s, full heading tree). You pick one or write a custom value. Each step has a Regenerate button + optional guidance text input ("shorter", "include free quote", "add an FAQ section") if the suggestions miss.',
      'Title and Meta validate that the primary keyword is present (and meta detects a CTA). Length is shown as a soft warning, not enforced.',
      'Verify Heading Keywords runs after the heading tree is locked: it scans your locked headings to check which target keywords are covered. You see which keywords are still missing.',
      'Lock Targets fixes the per-keyword target counts that the Content tab must hit.'
    ],
    outputs: [
      'A `heading_meta_lock` with `locked_title_tag`, `locked_meta_description`, `locked_h1`, `locked_heading_structure`, `locked_targets`, and `targets_validated`. Everything Content needs.',
      'Lock approval unlocks the Content tab.'
    ],
    gotchas: [
      'Title and meta length are shown as red/amber/gray badges (60 / 155 char targets) but are NOT enforced — you can lock something over-length if you want. The badges are just a hint.',
      'Title MUST contain the primary keyword. Meta MUST contain the primary keyword AND a call-to-action ("Learn more", "Get started", "Call today", etc.). These are enforced.',
      'Re-generating heading structure replaces your inline edits — there\'s a heads-up amber banner on that step.',
      'Each step has an Unlock button if you need to go back and re-do it.'
    ]
  },

  content: {
    label: 'Content',
    purpose: "Produce optimized content that honors everything you've locked above — the title, meta, H1, heading structure, target keyword counts, and topic commitments. The output is a deliverable ready to publish to WordPress.",
    inputs: [
      'A fully completed Structure tab (all locks set, targets validated).',
      'All upstream context (intent, format, competitor benchmarks, topic plan, counting deltas).'
    ],
    process: [
      'An LLM rewrites the page content section by section, hitting target keyword counts, covering the committed topics, fitting the locked heading structure.',
      'A density audit runs across the rewritten content to verify no over-optimization slipped in.',
      'A FAQ generator builds answers to the PAA questions captured in SERP analysis.',
      'A schema generator emits structured-data JSON-LD for the page (article / service / FAQ depending on type).',
      'A final audit scores the deliverable (heading quality, keyword coverage, word count vs benchmark) and surfaces any remaining gaps.',
      'You accept the deliverable. The page transitions to `deliverable_accepted` and the "Publish to WP" button appears in the page header.'
    ],
    outputs: [
      'An optimized content draft, density audit, FAQ block, schema JSON-LD, and a final audit score.',
      'A `deliverable_accepted` status that enables Publish to WP.'
    ],
    gotchas: [
      'Review the deliverable carefully before publishing. The AI honors the locks but can still pick odd phrasings.',
      '"Publish to WP" requires an active WordPress connection on the website. If the button is greyed out, check the website\'s WP integration settings.',
      'Republishing overwrites the live page. There is no automatic backup.'
    ]
  }
}
